import { Color } from '@persona-id/design-tokens';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { formatDate, formatDollar, formatNumber } from 'dashboard/lib/utils/formatters';
import { BankStatementData } from 'dashboard/models/document';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import TabBar, { Tab } from 'design-system/components/TabBar';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { SHORT_FORM_DATE } from 'lib/constants/luxonFormats';

const redactedSticker = <Sticker>Redacted</Sticker>;

const RedactedTableCell = styled(TableCell)`
  text-align: center;
`;

interface BankStatementBalancesProps {
  statement: BankStatementData;
  isRedacted?: boolean;
}

const formatEmptyDate = (date: string | undefined, format?: string) => {
  if (date || date !== '') {
    return formatDate(date, format);
  }
  return null;
};

const formatEmptyDollar = (dollar?: number) => {
  if (dollar || dollar === 0) {
    return formatDollar(dollar);
  }
  return null;
};

const formatEmptyNumber = (number?: number) => {
  if (number || number === 0) {
    return formatNumber(number, { minimumFractionDigits: 2 });
  }
  return null;
};

const BankStatementBalances = ({ statement, isRedacted = false }: BankStatementBalancesProps) => {
  const [monthTab, setMonthTab] = useState<{ year: number; month: number } | null>(null);

  useEffect(() => {
    if (
      monthTab != null &&
      statement['monthly-data'].every(
        ({ year, month }) => monthTab.year !== year || monthTab.month !== month,
      )
    ) {
      setMonthTab(null);
    }
  }, [monthTab, statement]);

  const data =
    monthTab == null
      ? statement['combined-data']
      : statement['monthly-data'].find(
          ({ year, month }) => monthTab.year === year && monthTab.month === month,
        );

  return (
    <Card style={{ border: `1px solid ${Color.BorderHairline}` }}>
      <Card.Body>
        <Card.Title>Balances</Card.Title>
        {!isRedacted && (
          <>
            <TabBar>
              <Tab
                id='bank-statement-balances__tab-all'
                selected={monthTab == null}
                onClick={() => setMonthTab(null)}
              >
                All
              </Tab>
              {statement?.['monthly-data'].map(({ year, month }) => (
                <Tab
                  key={`bank-statement-balances__tab-${year}-${month}`}
                  id={`bank-statement-balances__tab-${year}-${month}`}
                  selected={monthTab?.year === year && monthTab?.month === month}
                  onClick={() => setMonthTab({ year, month })}
                >
                  {formatDate(`${year}-${month}-01`, 'MMM YYYY')}
                </Tab>
              ))}
            </TabBar>
            <Spacer height={1} />
          </>
        )}
        <section>
          <Card.SectionHeader>Summary</Card.SectionHeader>
          <AttributeGrid horizontal='full-width'>
            <Attribute
              label='Beginning date'
              value={formatEmptyDate(data?.['computed-data']?.['calculated-beginning-date'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Ending date'
              value={formatEmptyDate(data?.['computed-data']?.['calculated-ending-date'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Calculated ending balance'
              value={formatEmptyDollar(data?.['computed-data']?.['calculated-ending-balance'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Estimated revenue'
              value={formatEmptyDollar(data?.['computed-data']?.['total-estimated-revenue'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Average daily balance'
              value={formatEmptyDollar(data?.['computed-data']?.['average-daily-balance'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Average weekdays balance'
              value={formatEmptyDollar(data?.['computed-data']?.['average-weekday-balance'])}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Days with negative balance'
              value={data?.['computed-data']?.['days-with-negative-balance']?.length}
              placeholder='Unknown'
              redacted={isRedacted}
            />
            <Attribute
              label='Weekdays with negative balance'
              value={data?.['computed-data']?.['weekdays-with-negative-balance']?.length}
              placeholder='Unknown'
              redacted={isRedacted}
            />
          </AttributeGrid>
        </section>
        <Card.CollapsibleSection title='Daily balance'>
          <Spacer height={1.5} />
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Date</TableHeaderCell>
                <TableHeaderCell>Amount</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isRedacted ? (
                <TableRow>
                  <RedactedTableCell colSpan={2}>{redactedSticker}</RedactedTableCell>
                </TableRow>
              ) : (
                data?.['computed-data']?.['daily-balances']?.map((dailyBalance, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      {formatEmptyDate(dailyBalance?.date, SHORT_FORM_DATE) || 'Unknown'}
                    </TableCell>
                    <TableCell>{formatEmptyNumber(dailyBalance?.balance) ?? 'Unknown'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card.CollapsibleSection>
      </Card.Body>
    </Card>
  );
};

export default BankStatementBalances;
